import type { ActionUrgency, Cabinet, Membership, OutstandingAction } from '@mfos/shared';
import { Link } from 'react-router';
import { Badge, StatusBadge } from '../../components/Badge';
import { ErrorNotice, LoadingNotice } from '../../components/ErrorNotice';
import { formatDate, formatDateTime, formatPercent } from '../../lib/format';
import { phrase, roleLabel, useI18n } from '../../lib/i18n/runtime';
import { useUserTimeZone } from '../auth/auth.api';
import { EquityPositionDetails } from '../equity/EquityPositionDetails';
import { formatMoney } from '../finance/finance-display';
import { MoneyText } from '../finance/finance-format';
import { ObligationList } from '../obligations/ObligationList';
import { FinanceFactsView, MyDistributions } from '../reports/FinanceFacts';
import { usePersonalPosition } from '../reports/reports.api';
import { taskRef } from '../tasks/task-display';
import { TaskDue, TaskStatusBadge } from '../tasks/task-format';
import { ReportStateBadge } from '../updates/update-format';
import { can, useMembership } from '../workspace/membership-context';
import { sortOutstandingActions, useCabinet } from './cabinet.api';

const URGENCY_DISPLAY: Readonly<
  Record<ActionUrgency, { label: string; tone: 'down' | 'warn' | 'info' }>
> = {
  URGENT: { label: 'Urgent', tone: 'down' },
  SOON: { label: 'Soon', tone: 'warn' },
  INFO: { label: 'For information', tone: 'info' },
};

function UrgencyBadge({ urgency }: { urgency: ActionUrgency }) {
  const { t } = useI18n();
  const display = URGENCY_DISPLAY[urgency];
  const label =
    urgency === 'URGENT' ? t('urgent') : urgency === 'SOON' ? t('soon') : t('forInformation');
  return <Badge tone={display.tone} label={label} />;
}

function actionLink(action: OutstandingAction, membership: Membership): string | null {
  const base = `/w/${membership.workspace.id}`;
  switch (action.kind) {
    case 'MFA_ENROLLMENT_REQUIRED':
    case 'MFA_RECOMMENDED':
      return '/security';
    case 'OBLIGATION_DUE_SOON':
      return membership.founderProfileId !== null
        ? `${base}/founders/${membership.founderProfileId}`
        : null;
    case 'CAP_TABLE_DRAFT_PENDING':
      return can(membership, 'equity.manage') ? `${base}/admin/equity` : null;
    case 'FOUNDER_PROFILE_MISSING':
      return can(membership, 'founders.manage') ? `${base}/admin/founders` : null;
    case 'INVITATION_PENDING':
      return can(membership, 'members.invite') ? `${base}/admin/invitations` : null;
    case 'VOTE_PENDING':
    case 'ROUND_AWAITING_FINALIZATION':
      return action.subjectId !== null ? `${base}/topics/${action.subjectId}` : `${base}/topics`;
    case 'DECISION_DRAFT_PENDING':
      return action.subjectId !== null
        ? `${base}/decisions/${action.subjectId}`
        : `${base}/decisions`;
    case 'TASK_OVERDUE':
    case 'TASK_DUE_SOON':
      return action.subjectId !== null ? `${base}/tasks/${action.subjectId}` : `${base}/tasks`;
    case 'BLOCKER_WAITING':
      return `${base}/blockers?view=WAITING_ON_ME`;
    case 'WEEKLY_UPDATE_DUE':
      return action.subjectId !== null
        ? `${base}/updates?period=${action.subjectId}`
        : `${base}/updates`;
    case 'FUNDING_CONTRIBUTION_DUE':
      return action.subjectId !== null
        ? `${base}/finance/funding/calls/${action.subjectId}`
        : `${base}/finance/funding`;
  }
}

export function CabinetPage() {
  const membership = useMembership();
  const cabinet = useCabinet(membership.workspace.id);
  const { t } = useI18n();

  if (cabinet.isPending) {
    return <LoadingNotice>{t('loadingCabinet')}</LoadingNotice>;
  }
  if (cabinet.isError) {
    return <ErrorNotice error={cabinet.error} title={t('cabinetError')} />;
  }
  return <CabinetView cabinet={cabinet.data} membership={membership} />;
}

function CompanyPosition({ workspaceId }: { workspaceId: string }) {
  const position = usePersonalPosition(workspaceId);
  const { t } = useI18n();
  const base = `/w/${workspaceId}`;
  if (position.isPending) return <LoadingNotice>{t('loadingFigures')}</LoadingNotice>;
  if (position.isError) {
    return <ErrorNotice error={position.error} title={t('figuresError')} />;
  }
  const data = position.data;
  return (
    <div className="stack">
      <h2>{t('companyPosition')}</h2>
      <FinanceFactsView facts={data} base={base} />
      {data.myDistributions !== null && (
        <MyDistributions distributions={data.myDistributions} workspaceId={workspaceId} />
      )}
    </div>
  );
}

/** Personal funding position; amounts are system-calculated, shown in words as well as numbers. */
function MyFinances({ funding, link }: { funding: NonNullable<Cabinet['funding']>; link: string }) {
  return (
    <section className="card stack" aria-labelledby="my-finances-heading">
      <h2 id="my-finances-heading">{phrase('My finances')}</h2>
      {funding.balances.length === 0 ? (
        <p className="meta">{phrase('You have no funding shares, debts or credits.')}</p>
      ) : (
        <ul className="plain-list">
          {funding.balances.map((balance) => (
            <li key={balance.currency}>
              {phrase('I owe')}{' '}
              <MoneyText money={{ amount: balance.owedByMe, currency: balance.currency }} />
              {' · '}owed to me{' '}
              <MoneyText money={{ amount: balance.owedToMe, currency: balance.currency }} />
              {/[1-9]/.test(balance.overdue) && (
                <>
                  {' · '}
                  <Badge
                    tone="down"
                    label={`${formatMoney({ amount: balance.overdue, currency: balance.currency })} overdue`}
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {funding.openContributions > 0 && (
        <p>
          {funding.openContributions} funding share{funding.openContributions === 1 ? '' : 's'} to
          pay
          {funding.overdueContributions > 0
            ? `, ${String(funding.overdueContributions)} overdue`
            : ''}
          .
        </p>
      )}
      <p>
        <Link to={link}>{phrase('Open my finances')}</Link>
      </p>
    </section>
  );
}

function CabinetView({ cabinet, membership }: { cabinet: Cabinet; membership: Membership }) {
  const timeZone = useUserTimeZone();
  const { t } = useI18n();
  const actions = sortOutstandingActions(cabinet.outstandingActions);
  const base = `/w/${membership.workspace.id}`;

  return (
    <div className="stack stack--lg">
      <div>
        <h1>{cabinet.workspace.name}</h1>
        <p className="lead">
          {cabinet.member.displayName} · {roleLabel(t, cabinet.member.role)}
          {cabinet.member.title !== null ? ` · ${cabinet.member.title}` : ''}
        </p>
        {can(membership, 'reports.read') && (
          <p>
            <Link to={`${base}/reports`}>{t('workspaceReports')}</Link>
          </p>
        )}
      </div>

      <section className="card" aria-labelledby="waiting-heading">
        <h2 id="waiting-heading">{t('waiting')}</h2>
        {actions.length === 0 ? (
          <p className="meta">{t('nothingWaiting')}</p>
        ) : (
          <ul className="item-list" aria-label={t('outstandingList')}>
            {actions.map((action, index) => {
              const link = actionLink(action, membership);
              const title =
                action.kind === 'MFA_RECOMMENDED' ? t('mfaRecommendedTitle') : action.title;
              const detail =
                action.kind === 'MFA_RECOMMENDED' ? t('mfaRecommendedDetail') : action.detail;
              return (
                <li key={`${action.kind}:${action.subjectId ?? String(index)}`} className="item">
                  <div className="item__header">
                    <UrgencyBadge urgency={action.urgency} />
                    <h3 className="item__title">
                      {link !== null ? <Link to={link}>{title}</Link> : title}
                    </h3>
                  </div>
                  {detail !== null && <p>{detail}</p>}
                  {action.dueAt !== null && (
                    <p className="meta">
                      {t('due')}{' '}
                      <time dateTime={action.dueAt}>{formatDateTime(action.dueAt, timeZone)}</time>
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {cabinet.weeklyUpdate !== null && (
        <section className="card" aria-labelledby="weekly-update-heading">
          <div className="item__header">
            <h2 id="weekly-update-heading">{phrase('My weekly update')}</h2>
            <ReportStateBadge state={cabinet.weeklyUpdate.state} />
          </div>
          <p>
            {formatDate(cabinet.weeklyUpdate.startsOn)} – {formatDate(cabinet.weeklyUpdate.endsOn)}{' '}
            · due{' '}
            <time dateTime={cabinet.weeklyUpdate.dueAt}>
              {formatDateTime(cabinet.weeklyUpdate.dueAt, timeZone)}
            </time>
          </p>
          <p>
            <Link to={`${base}/updates?period=${cabinet.weeklyUpdate.periodId}`}>
              {['SUBMITTED', 'LATE'].includes(cabinet.weeklyUpdate.state)
                ? phrase('View or amend my update')
                : phrase('Write my update')}
            </Link>
          </p>
        </section>
      )}

      {can(membership, 'tasks.read') && (
        <section className="card" aria-labelledby="my-tasks-heading">
          <h2 id="my-tasks-heading">{t('myTasks')}</h2>
          {cabinet.myTasks.length === 0 ? (
            <p className="meta">{t('noTasks')}</p>
          ) : (
            <ul className="item-list" aria-label={t('myTasksList')}>
              {cabinet.myTasks.map((task) => (
                <li key={task.id} className="item">
                  <div className="item__header">
                    <h3 className="item__title">
                      <Link to={`${base}/tasks/${task.id}`}>
                        <span className="ref">{taskRef(task.number)}</span> {task.title}
                      </Link>
                    </h3>
                  </div>
                  <div className="badge-row">
                    <TaskStatusBadge status={task.status} />
                    {task.openBlockerCount > 0 && <Badge tone="down" label={t('blocked')} />}
                  </div>
                  <p className="meta">
                    {phrase('Due')} <TaskDue task={task} timeZone={timeZone} />
                  </p>
                </li>
              ))}
            </ul>
          )}
          <p>
            <Link to={`${base}/tasks`}>{t('allTasks')}</Link>
          </p>
        </section>
      )}

      {cabinet.funding !== null && cabinet.founder !== null && (
        <MyFinances
          funding={cabinet.funding}
          link={`${base}/finance/funding/founders/${cabinet.founder.profileId}`}
        />
      )}

      {can(membership, 'finance.read') && <CompanyPosition workspaceId={membership.workspace.id} />}

      <section className="card" aria-labelledby="equity-heading">
        <h2 id="equity-heading">{t('myEquity')}</h2>
        {cabinet.founder === null ? (
          <p className="meta">{t('noFounderProfile')}</p>
        ) : (
          <>
            {cabinet.founder.currentEquity !== null ? (
              <EquityPositionDetails position={cabinet.founder.currentEquity} timeZone={timeZone} />
            ) : (
              <p className="meta">{phrase('No approved cap table includes you yet.')}</p>
            )}
            {cabinet.founder.pendingEquityChange !== null && (
              <div className="notice notice--info pending-change">
                <span aria-hidden="true">ℹ </span>
                <strong>{phrase('Pending change — not yet approved.')}</strong> Draft cap table
                version #{cabinet.founder.pendingEquityChange.versionNumber}, effective{' '}
                <time dateTime={cabinet.founder.pendingEquityChange.effectiveFrom}>
                  {formatDate(cabinet.founder.pendingEquityChange.effectiveFrom)}
                </time>
                , would set your ownership to{' '}
                {cabinet.founder.pendingEquityChange.percentage !== null
                  ? formatPercent(cabinet.founder.pendingEquityChange.percentage)
                  : 'no share (removed)'}
                .
              </div>
            )}
            <p className="meta">
              {phrase('Founder status:')} <StatusBadge status={cabinet.founder.status} />
              {cabinet.founder.founderSince !== null && (
                <> · founder since {formatDate(cabinet.founder.founderSince)}</>
              )}
            </p>
          </>
        )}
      </section>

      <section className="card" aria-labelledby="responsibilities-heading">
        <h2 id="responsibilities-heading">{t('responsibilities')}</h2>
        <ObligationList
          obligations={cabinet.responsibilities}
          timeZone={cabinet.workspace.timezone}
        />
      </section>

      <section className="card" aria-labelledby="membership-heading">
        <h2 id="membership-heading">{t('membership')}</h2>
        <dl className="summary-list">
          <div>
            <dt>{t('role')}</dt>
            <dd>{roleLabel(t, cabinet.member.role)}</dd>
          </div>
          <div>
            <dt>{t('title')}</dt>
            <dd>{cabinet.member.title ?? t('notSet')}</dd>
          </div>
          <div>
            <dt>{t('status')}</dt>
            <dd>
              <StatusBadge status={cabinet.member.status} />
            </dd>
          </div>
          <div>
            <dt>{t('joined')}</dt>
            <dd>
              <time dateTime={cabinet.member.joinedAt}>
                {formatDateTime(cabinet.member.joinedAt, timeZone)}
              </time>
            </dd>
          </div>
          <div>
            <dt>{t('twoStep')}</dt>
            <dd>
              {cabinet.security.mfaEnabled ? (
                <Badge tone="ok" label={t('enabled')} />
              ) : (
                <Badge
                  tone={cabinet.security.mfaRequired ? 'down' : 'warn'}
                  label={t('notEnabled')}
                />
              )}{' '}
              <Link to="/security">{t('manage')}</Link>
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
